import { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { DashboardWrapper } from '../modules/apps/dashboard/DashboardWrapper'
import CompanyPage from '../modules/company/CompanyPage'
import SettingsPage from '../modules/settings/SettingsPage'
import DataPage from '../modules/data/DataPage'
import DataViewPage from '../modules/data/DataViewPage'
import { useAuth } from '../modules/auth/core/AuthProvider'
import ReportsPage from '../modules/reports/ReportsPage'
import NotificationsPage from '../modules/notifications/NotificationsPage'
import DocumentPage from '../modules/documents/DocumentPage'
import DataRequestsPage from '../modules/data/DataRequestsPage'
import DataExportPage from '../modules/data/DataExportPage'
import LoraPage from '../modules/lora/LoraPage'
import SecrReportsPage from '../modules/secr_reports/SecrReportsPage'
import SecrDocumentsPage from '../modules/secr_documents/SecrDocumentsPage'
import BenchmarkPage from '../modules/benchmark/BenchmarkPage'
import CompanyTasksPage from '../modules/company_tasks/CompanyTasksPage'
import CompanyOverviewPage from '../modules/company_overview/CompanyOverviewPage'
import SingleUserReportsPage from '../modules/reports/SingleUserReportsPage'
import SingleUserBenchmarkPage from '../modules/benchmark/SingleUserBenchmarkPage'
import GroupedAlarmsPage from '../modules/alarms/pages/GroupedAlarmsPage'
import SimulationPage from '../modules/analytics/SimulationPage'
import ProfilingPage from '../modules/analytics/ProfilingPage'
import EventsPage from '../modules/events/EventsPage'
import ProfilesPage from '../modules/profiles/ProfilesPage'
import SimulationsPage from '../modules/simulations/SimulationsPage'
import LiveDataPage from '../modules/lora/LiveDataPage'
import DataReliabilityPage from '../modules/data/DataReliabilityPage'

const PrivateRoutes = () => {
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const { user } = useAuth()
  const scopeLevel = user?.scope?.level ?? 'none'
  const canViewScopedModules = scopeLevel !== 'none'
  const canManageUsers = user?.permissions?.canManageUsers ?? false
  const canManageCompanyData = user?.permissions?.canManageCompanyData ?? false
  const canManageSiteData = user?.permissions?.canManageSiteData ?? false
  const canAccessSettings = user?.permissions?.canAccessSettings ?? false
  const canAccessDataExport = user?.permissions?.canAccessDataExport ?? false
  const canAccessDataFiles = user?.permissions?.canAccessDataFiles ?? false
  const canAccessDataView = user?.permissions?.canAccessDataView ?? false
  const canAccessDataRequests = user?.permissions?.canAccessDataRequests ?? false
  const canAccessDataReliability = user?.permissions?.canAccessDataReliability ?? false
  const canAccessSecrReports = user?.permissions?.canAccessSecrReports ?? false
  const canAccessLoraDataCollections = user?.permissions?.canAccessLoraDataCollections ?? false
  const canAccessLoraLiveData = user?.permissions?.canAccessLoraLiveData ?? false
  const canAccessTaskManagement = user?.permissions?.canAccessTaskManagement ?? false
  const canViewGroupDashboard = user?.permissions?.canViewGroupDashboard ?? false

  if (!canManageCompanyData) {
    return (
      <Routes>
        <Route element={<MasterLayout />}>
          <Route path='auth/*' element={<Navigate to='/dashboard' />} />
          <Route path='dashboard' element={<DashboardWrapper />} />
          {canViewGroupDashboard && <Route path='dashboard/group' element={<DashboardWrapper />} />}
          {canManageSiteData && (
            <Route
              path='company/*'
              element={
                <SuspensedView>
                  <CompanyPage />
                </SuspensedView>
              }
            />
          )}
          {canAccessDataExport && (
            <Route
              path='data_export/*'
              element={
                <SuspensedView>
                  <DataExportPage />
                </SuspensedView>
              }
            />
          )}
          {canViewScopedModules && (
            <Route
              path='reports/*'
              element={
                <SuspensedView>
                  <SingleUserReportsPage />
                </SuspensedView>
              }
            />
          )}
          {canViewScopedModules && (
            <Route
              path='alarms/grouped'
              element={
                <SuspensedView>
                  <GroupedAlarmsPage />
                </SuspensedView>
              }
            />
          )}
          {canViewScopedModules && (
            <Route
              path='benchmark/*'
              element={
                <SuspensedView>
                  <SingleUserBenchmarkPage />
                </SuspensedView>
              }
            />
          )}
          {canViewScopedModules && canAccessLoraLiveData && (
            <Route
              path='lora/live-data'
              element={
                <SuspensedView>
                  <LiveDataPage />
                </SuspensedView>
              }
            />
          )}
          <Route path='*' element={<Navigate to='/error/404' />} />
        </Route>
      </Routes>
    )
  }
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        <Route path='dashboard' element={<DashboardWrapper />} />
        {canViewGroupDashboard && <Route path='dashboard/group' element={<DashboardWrapper />} />}

        {canManageUsers && (
          <Route
            path='users/*'
            element={
              <SuspensedView>
                <UsersPage />
              </SuspensedView>
            }
          />
        )}
        {canManageCompanyData && (
          <Route
            path='company/*'
            element={
              <SuspensedView>
                <CompanyPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessDataFiles && (
          <Route
            path='data_files/*'
            element={
              <SuspensedView>
                <DataPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessDataView && (
          <Route
            path='data_view/*'
            element={
              <SuspensedView>
                <DataViewPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessDataRequests && (
          <Route
            path='data_requests/*'
            element={
              <SuspensedView>
                <DataRequestsPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessDataExport && (
          <Route
            path='data_export/*'
            element={
              <SuspensedView>
                <DataExportPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessDataReliability && (
          <Route
            path='data_reliability'
            element={
              <SuspensedView>
                <DataReliabilityPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessSettings && (
          <Route
            path='settings/*'
            element={
              <SuspensedView>
                <SettingsPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='reports/*'
          element={
            <SuspensedView>
              <ReportsPage />
            </SuspensedView>
          }
        />
        <Route
          path='documents/*'
          element={
            <SuspensedView>
              <DocumentPage />
            </SuspensedView>
          }
        />
        <Route
          path='notifications/*'
          element={
            <SuspensedView>
              <NotificationsPage />
            </SuspensedView>
          }
        />
        {canAccessLoraDataCollections && (
          <Route
            path='lora/*'
            element={
              <SuspensedView>
                <LoraPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessSecrReports && (
          <Route
            path='secr_reports/*'
            element={
              <SuspensedView>
                <SecrReportsPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessSecrReports && (
          <Route
            path='secr_documents/*'
            element={
              <SuspensedView>
                <SecrDocumentsPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='benchmark/*'
          element={
            <SuspensedView>
              <BenchmarkPage />
            </SuspensedView>
          }
        />
        {canAccessTaskManagement && (
          <Route
            path='company_tasks/*'
            element={
              <SuspensedView>
                <CompanyTasksPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='company_overview/*'
          element={
            <SuspensedView>
              <CompanyOverviewPage />
            </SuspensedView>
          }
        />
        <Route
          path='analytics/simulation'
          element={
            <SuspensedView>
              <SimulationPage />
            </SuspensedView>
          }
        />
        <Route
          path='analytics/profiling'
          element={
            <SuspensedView>
              <ProfilingPage />
            </SuspensedView>
          }
        />
        <Route
          path='alarms/grouped'
          element={
            <SuspensedView>
              <GroupedAlarmsPage />
            </SuspensedView>
          }
        />
        {canAccessSettings && (
          <Route
            path='events/*'
            element={
              <SuspensedView>
                <EventsPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessSettings && (
          <Route
            path='profiles/*'
            element={
              <SuspensedView>
                <ProfilesPage />
              </SuspensedView>
            }
          />
        )}
        {canAccessSettings && (
          <Route
            path='simulations/*'
            element={
              <SuspensedView>
                <SimulationsPage />
              </SuspensedView>
            }
          />
        )}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<any> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
